import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
 UserCircleIcon,
 CogIcon,
 KeyIcon,
 TrashIcon,
 CheckIcon,
 XMarkIcon,
 ExclamationTriangleIcon,
 EyeIcon,
 EyeSlashIcon
} from '@heroicons/react/24/outline';
// Validation functions (following existing app pattern)
const validatePhone = (phone) => {
 const phoneRegex = /^03\d{9}$/;
 return phoneRegex.test(phone.replace(/\s/g, ''));
};

const validatePassword = (password) => {
 return password.length >= 8;
};

const validateRequired = (value) => {
 return value && value.toString().trim() !== '';
};

const validateIntermediateMarks = (marks) => {
 if (!marks || marks === '') return true;
 const numMarks = parseFloat(marks);
 return !isNaN(numMarks) && numMarks >= 0 && numMarks <= 100;
};

const validatePostalCode = (postalCode) => {
 if (!postalCode || postalCode === '') return true;
 return /^\d{5}$/.test(postalCode);
};

const Settings = () => {
 const { user, isAuthenticated, isStudent, isUniversity, logout } = useAuth();
 const navigate = useNavigate();
 const [activeTab, setActiveTab] = useState('personal');
 const [loading, setLoading] = useState(true);
 const [profileData, setProfileData] = useState(null);
 const [fieldsOfStudy, setFieldsOfStudy] = useState([]);
 const [universityTypes, setUniversityTypes] = useState([]);
 const [cities, setCities] = useState([]);

 // Form states
 const [personalInfo, setPersonalInfo] = useState({});
 const [passwordData, setPasswordData] = useState({
 oldPassword: '',
 newPassword: '',
 confirmPassword: ''
 });
 const [showDeleteModal, setShowDeleteModal] = useState(false);
 const [deleteConfirmText, setDeleteConfirmText] = useState('');

 // Password visibility states
 const [showOldPassword, setShowOldPassword] = useState(false);
 const [showNewPassword, setShowNewPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);

 // UI states
 const [saving, setSaving] = useState(false);
 const [message, setMessage] = useState({ type: '', text: '' });
 const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
 const [validationErrors, setValidationErrors] = useState({});
 const [passwordValidationErrors, setPasswordValidationErrors] = useState({});

 useEffect(() => {
 document.title = 'Settings - Dakhla';
 }, []);

 useEffect(() => {
 if (isStudent) {
 setActiveTab('account');
 }
 }, [isStudent]);

 useEffect(() => {
 if (!isAuthenticated) {
 navigate('/login');
 return;
 }
 fetchProfileData();
 fetchDropdownData();
 }, [isAuthenticated, navigate]);

 const fetchProfileData = async () => {
 try {
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const token = localStorage.getItem('token');
 const endpoint = isStudent
 ? `${apiUrl}/student/profile`
 : `${apiUrl}/university/profile`;

 const response = await fetch(endpoint, {
 headers: {
 'Authorization': `Bearer ${token}`
 }
 });

 if (response.ok) {
 const data = await response.json();
 setProfileData(data);

 // Map the profile data to the correct field names for the form
 if (isStudent) {
 setPersonalInfo({
 name: data.name,
 email: data.email,
 phone: data.phone,
 address_line: data.address_line,
 city_id: data.city_id,
 postal_code: data.postal_code,
 discipline: data.discipline,
 field_of_interest_id: data.field_of_interest_id,
 intermediate_marks: data.intermediate_marks
 });
 } else {
 setPersonalInfo({
 university_name: data.university_name,
 contact_person: data.name, // Contact person name is stored as 'name' in profile
 email: data.email,
 phone: data.phone,
 address: data.address,
 established_year: data.established_year,
 university_type_id: data.university_type_id
 });
 }
 } else {
 const errorData = await response.json();
 console.error('Profile fetch error response:', errorData); // Detailed error log
 setMessage({
 type: 'error',
 text: errorData.error ? `Error: ${errorData.error}` : `Failed to load profile data (${response.status})`
 });
 }
 } catch (error) {
 console.error('Error fetching profile (network/parsing):', error);
 setMessage({ type: 'error', text: `Failed to load profile data: ${error.message}` });
 } finally {
 setLoading(false);
 }
 };

 const fetchDropdownData = async () => {
 try {
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const [fieldsRes, typesRes, citiesRes] = await Promise.all([
 fetch(`${apiUrl}/fields-of-study`),
 fetch(`${apiUrl}/university-types`),
 fetch(`${apiUrl}/cities`)
 ]);

 if (fieldsRes.ok) {
 const fieldsData = await fieldsRes.json();
 setFieldsOfStudy(fieldsData);
 }

 if (typesRes.ok) {
 const typesData = await typesRes.json();
 setUniversityTypes(typesData);
 }

 if (citiesRes.ok) {
 const citiesData = await citiesRes.json();
 setCities(citiesData);
 }
 } catch (error) {
 console.error('Error fetching dropdown data:', error);
 }
 };

 const handlePersonalInfoChange = (field, value) => {
 setPersonalInfo(prev => ({
 ...prev,
 [field]: value
 }));
 setHasUnsavedChanges(true);
 setMessage({ type: '', text: '' });

 // Clear validation error for this field
 if (validationErrors[field]) {
 setValidationErrors(prev => {
 const newErrors = { ...prev };
 delete newErrors[field];
 return newErrors;
 });
 }
 };

 const handlePasswordChange = (field, value) => {
 setPasswordData(prev => ({
 ...prev,
 [field]: value
 }));

 // Clear validation error for this field
 if (passwordValidationErrors[field]) {
 setPasswordValidationErrors(prev => {
 const newErrors = { ...prev };
 delete newErrors[field];
 return newErrors;
 });
 }

 // Clear general message
 setMessage({ type: '', text: '' });
 };

 const savePersonalInfo = async () => {
 // Validate form data (following existing app pattern)
 const errors = {};

 if (isStudent) {
 // Student validation
 if (!validateRequired(personalInfo.name)) {
 errors.name = 'Full name is required';
 }
 if (personalInfo.phone && !validatePhone(personalInfo.phone)) {
 errors.phone = 'Phone number must be in Pakistani format (03XXXXXXXXX)';
 }
 if (personalInfo.intermediate_marks && !validateIntermediateMarks(personalInfo.intermediate_marks)) {
 errors.intermediate_marks = 'Marks must be between 0 and 100';
 }
 if (personalInfo.postal_code && !validatePostalCode(personalInfo.postal_code)) {
 errors.postal_code = 'Postal code must be exactly 5 digits';
 }
 } else {
 // University validation
 if (!validateRequired(personalInfo.contact_person)) {
 errors.contact_person = 'Contact person name is required';
 }
 if (personalInfo.phone && !validatePhone(personalInfo.phone)) {
 errors.phone = 'Phone number must be in Pakistani format (03XXXXXXXXX)';
 }
 if (!validateRequired(personalInfo.address)) {
 errors.address = 'University address is required';
 }
 }

 if (Object.keys(errors).length > 0) {
 setValidationErrors(errors);
 setMessage({
 type: 'error',
 text: 'Please fix the validation errors below'
 });
 return;
 }

 setSaving(true);
 setMessage({ type: '', text: '' });
 setValidationErrors({});

 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const endpoint = isStudent
 ? `${apiUrl}/student/profile`
 : `${apiUrl}/university/profile`;

 const response = await fetch(endpoint, {
 method: 'PUT',
 headers: {
 'Content-Type': 'application/json',
 'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify(personalInfo)
 });

 if (response.ok) {
 setMessage({ type: 'success', text: 'Profile updated successfully!' });
 setHasUnsavedChanges(false);
 // Refresh profile data
 await fetchProfileData();
 } else {
 const errorData = await response.json();
 setMessage({ type: 'error', text: errorData.error || 'Failed to update profile' });
 }
 } catch (error) {
 console.error('Error updating profile:', error);
 setMessage({ type: 'error', text: 'Failed to update profile' });
 } finally {
 setSaving(false);
 }
 };

 const changePassword = async () => {
 // Validate password data (following existing app pattern)
 const errors = {};

 if (!validateRequired(passwordData.oldPassword)) {
 errors.oldPassword = 'Current password is required';
 }
 if (!validateRequired(passwordData.newPassword)) {
 errors.newPassword = 'New password is required';
 } else if (!validatePassword(passwordData.newPassword)) {
 errors.newPassword = 'Password must be at least 8 characters long';
 }
 if (!validateRequired(passwordData.confirmPassword)) {
 errors.confirmPassword = 'Confirm password is required';
 } else if (passwordData.newPassword !== passwordData.confirmPassword) {
 errors.confirmPassword = 'Passwords do not match';
 }

 if (Object.keys(errors).length > 0) {
 setPasswordValidationErrors(errors);
 setMessage({
 type: 'error',
 text: 'Please fix the validation errors below'
 });
 return;
 }

 setSaving(true);
 setMessage({ type: '', text: '' });
 setPasswordValidationErrors({});

 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const endpoint = isStudent
 ? `${apiUrl}/student/change-password`
 : `${apiUrl}/university/change-password`;

 const response = await fetch(endpoint, {
 method: 'PUT',
 headers: {
 'Content-Type': 'application/json',
 'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify({
 oldPassword: passwordData.oldPassword,
 newPassword: passwordData.newPassword
 })
 });

 if (response.ok) {
 setMessage({ type: 'success', text: 'Password changed successfully!' });
 setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
 } else {
 const errorData = await response.json();
 setMessage({ type: 'error', text: errorData.error || 'Failed to change password' });
 }
 } catch (error) {
 console.error('Error changing password:', error);
 setMessage({ type: 'error', text: 'Failed to change password' });
 } finally {
 setSaving(false);
 }
 };

 const deleteAccount = async () => {
 const expectedText = user?.email || '';

 if (deleteConfirmText !== expectedText) {
 setMessage({ type: 'error', text: `Please type your email address "${expectedText}" to confirm` });
 return;
 }

 setSaving(true);
 setMessage({ type: '', text: '' });

 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const endpoint = isStudent
 ? `${apiUrl}/student/delete-account`
 : `${apiUrl}/university/delete-account`;

 const response = await fetch(endpoint, {
 method: 'DELETE',
 headers: {
 'Authorization': `Bearer ${token}`
 }
 });

 if (response.ok) {
 setMessage({ type: 'success', text: 'Account deleted successfully. Redirecting...' });
 setTimeout(() => {
 logout();
 navigate('/');
 }, 2000);
 } else {
 const errorData = await response.json();
 setMessage({ type: 'error', text: errorData.error || 'Failed to delete account' });
 }
 } catch (error) {
 console.error('Error deleting account:', error);
 setMessage({ type: 'error', text: 'Failed to delete account' });
 } finally {
 setSaving(false);
 }
 };

 const cancelChanges = () => {
 setPersonalInfo(profileData);
 setHasUnsavedChanges(false);
 setMessage({ type: '', text: '' });
 setValidationErrors({});
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-slate-50 flex items-center justify-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
 </div>
 );
 }

 const tabs = isStudent
 ? [
 { id: 'account', name: 'Settings', icon: CogIcon }
 ]
 : [
 { id: 'personal', name: 'University Profile', icon: UserCircleIcon },
 { id: 'account', name: 'Account Settings', icon: CogIcon }
 ];

 return (
 <div className="min-h-screen bg-slate-100 py-8">
 <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Unified Card Container */}
 <div className="card relative overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-sm">
 {/* Header (Top Bar) */}
 <div className="relative p-6 border-b border-slate-200">
 <div className="flex items-center space-x-4">
 <div className="h-16 w-16 bg-gradient-to-tr from-primary-50 to-primary-100/50 border border-primary-200/30 rounded-2xl flex items-center justify-center shadow-sm shadow-primary-500/5">
 <CogIcon className="h-8 w-8 text-primary-600" />
 </div>
 <div>
 <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Settings</h1>
 <p className="text-sm text-slate-500 mt-0.5">
 Manage your {isStudent ? 'student' : 'university'} account settings
 </p>
 </div>
 </div>
 </div>

 <div className="flex flex-col lg:flex-row min-h-[500px]">
 {/* Sidebar Navigation */}
 {!isStudent && (
 <div className="lg:w-1/4 border-r border-slate-200 p-6 bg-slate-50">
 <nav className="space-y-2">
 {tabs.map((tab) => {
 const Icon = tab.icon;
 return (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id)}
 className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-all duration-300 ${activeTab === tab.id
 ? 'bg-gradient-to-r from-primary-50 to-primary-100/50 text-primary-700 border-l-4 border-primary-600 font-semibold'
 : 'text-slate-600 hover:text-primary-600 hover:bg-slate-50'
 }`}
 >
 <Icon className="h-5 w-5" />
 <span className="font-medium">{tab.name}</span>
 </button>
 );
 })}
 </nav>
 </div>
 )}

 {/* Main Content */}
 <div className={`p-6 flex-1 ${isStudent ? "lg:w-full" : "lg:w-3/4"}`}>
 {/* Message Display */}
 {message.text && (
 <div className={`mb-6 p-4 rounded-xl text-sm font-semibold border transition-premium ${message.type === 'success'
 ? 'bg-green-50/50 border-green-200 text-green-700 shadow-sm shadow-green-500/5'
 : 'bg-red-50/50 border-red-200 text-red-700 shadow-sm shadow-red-500/5'
 }`}>
 <span className="flex items-center gap-2">
 <span className="text-base">{message.type === 'success' ? '✅' : '⚠️'}</span>
 <span>{message.text}</span>
 </span>
 </div>
 )}

 {/* Content Sections */}
 {activeTab === 'personal' && !isStudent && (
 <PersonalInfoSection
 isStudent={isStudent}
 personalInfo={personalInfo}
 fieldsOfStudy={fieldsOfStudy}
 universityTypes={universityTypes}
 cities={cities}
 onChange={handlePersonalInfoChange}
 onSave={savePersonalInfo}
 onCancel={cancelChanges}
 saving={saving}
 hasUnsavedChanges={hasUnsavedChanges}
 validationErrors={validationErrors}
 />
 )}

 {activeTab === 'account' && (
 <AccountSettingsSection
 isStudent={isStudent}
 passwordData={passwordData}
 onPasswordChange={handlePasswordChange}
 onChangePassword={changePassword}
 onDeleteAccount={() => setShowDeleteModal(true)}
 saving={saving}
 passwordValidationErrors={passwordValidationErrors}
 showOldPassword={showOldPassword}
 setShowOldPassword={setShowOldPassword}
 showNewPassword={showNewPassword}
 setShowNewPassword={setShowNewPassword}
 showConfirmPassword={showConfirmPassword}
 setShowConfirmPassword={setShowConfirmPassword}
 />
 )}
 </div>
 </div>
 </div>

 {/* Delete Account Modal */}
 {showDeleteModal && (
 <DeleteAccountModal
 isStudent={isStudent}
 userEmail={user?.email}
 deleteConfirmText={deleteConfirmText}
 setDeleteConfirmText={setDeleteConfirmText}
 onConfirm={deleteAccount}
 onCancel={() => {
 setShowDeleteModal(false);
 setDeleteConfirmText('');
 setMessage({ type: '', text: '' });
 }}
 saving={saving}
 />
 )}
 </div>
 </div>
 );
};

// Personal Information Section Component
const PersonalInfoSection = ({
 isStudent,
 personalInfo,
 fieldsOfStudy,
 universityTypes,
 cities,
 onChange,
 onSave,
 onCancel,
 saving,
 hasUnsavedChanges,
 validationErrors = {}
}) => {
 return (
 <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
 <h2 className="text-xl font-bold text-slate-800 mb-6">
 {isStudent ? 'Personal Information' : 'University Profile Information'}
 </h2>

 <div className="space-y-6">
 {isStudent ? (
 // Student Fields
 <>
 {/* Full Name */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Full Name
 </label>
 <input
 type="text"
 value={personalInfo.name || ''}
 onChange={(e) => onChange('name', e.target.value)}
 className={`w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm ${validationErrors.name ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`}
 placeholder="Enter your full name"
 />
 {validationErrors.name && (
 <p className="text-red-600 text-sm mt-1">{validationErrors.name}</p>
 )}
 </div>

 {/* Email (Non-editable) */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Email Address
 </label>
 <input
 type="email"
 value={personalInfo.email || ''}
 disabled
 className="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-slate-100 text-slate-500 cursor-not-allowed font-medium text-sm"
 />
 <p className="text-xs text-slate-400 mt-1">Email address cannot be changed</p>
 </div>

 {/* Phone Number */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Phone Number
 </label>
 <input
 type="tel"
 value={personalInfo.phone || ''}
 onChange={(e) => onChange('phone', e.target.value)}
 className={`w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm ${validationErrors.phone ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`}
 placeholder="03XXXXXXXXX"
 maxLength="11"
 />
 {validationErrors.phone ? (
 <p className="text-red-600 text-sm mt-1">{validationErrors.phone}</p>
 ) : (
 <p className="text-xs text-slate-400 mt-1">Pakistani format (11 digits)</p>
 )}
 </div>

 {/* Gender & Date of Birth (side by side) */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Gender
 </label>
 <select
 value={personalInfo.gender_id || ''}
 onChange={(e) => onChange('gender_id', e.target.value)}
 className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm cursor-pointer"
 >
 <option value="">Select Gender</option>
 <option value="1">Male</option>
 <option value="2">Female</option>
 <option value="3">Other</option>
 </select>
 </div>

 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Date of Birth
 </label>
 <input
 type="date"
 value={personalInfo.date_of_birth || ''}
 onChange={(e) => onChange('date_of_birth', e.target.value)}
 className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm"
 />
 </div>
 </div>

 {/* Address */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Address
 </label>
 <textarea
 value={personalInfo.address_line || ''}
 onChange={(e) => onChange('address_line', e.target.value)}
 rows="3"
 className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm"
 placeholder="Enter your address"
 />
 </div>

 {/* Postal Code */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Postal Code
 </label>
 <input
 type="text"
 value={personalInfo.postal_code || ''}
 onChange={(e) => {
 const value = e.target.value.replace(/\D/g, '').slice(0, 5);
 onChange('postal_code', value);
 }}
 maxLength="5"
 className={`w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm ${validationErrors.postal_code ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`}
 placeholder="Enter 5-digit postal code"
 />
 {validationErrors.postal_code ? (
 <p className="text-red-600 text-sm mt-1">{validationErrors.postal_code}</p>
 ) : (
 <p className="text-xs text-slate-400 mt-1">Pakistan postal code (5 digits)</p>
 )}
 </div>

 {/* City */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 City
 </label>
 <select
 value={personalInfo.city_id || ''}
 onChange={(e) => onChange('city_id', e.target.value)}
 className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm cursor-pointer"
 >
 <option value="">Select City</option>
 {cities.map(city => (
 <option key={city.id} value={city.id}>{city.name}</option>
 ))}
 </select>
 </div>

 {/* Discipline */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Discipline (Intermediate)
 </label>
 <select
 value={personalInfo.discipline || ''}
 onChange={(e) => onChange('discipline', e.target.value)}
 className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm cursor-pointer"
 >
 <option value="">Select Discipline</option>
 <option value="Pre-Medical">Pre-Medical</option>
 <option value="Pre-Engineering">Pre-Engineering</option>
 <option value="Computer Science">Computer Science</option>
 <option value="General Science">General Science</option>
 <option value="Humanities">Humanities</option>
 <option value="Commerce">Commerce</option>
 <option value="Home Economics">Home Economics</option>
 <option value="Pre-Agricultural">Pre-Agricultural</option>
 <option value="Para-Medical">Para-Medical</option>
 </select>
 <p className="text-xs text-slate-400 mt-1">Your intermediate/A-Level discipline</p>
 </div>

 {/* Preferred Field of Study */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Preferred Field of Study
 </label>
 <select
 value={personalInfo.field_of_interest_id || ''}
 onChange={(e) => onChange('field_of_interest_id', e.target.value)}
 className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm cursor-pointer"
 >
 <option value="">Select Field of Study</option>
 {fieldsOfStudy.map(field => (
 <option key={field.id} value={field.id}>{field.name}</option>
 ))}
 </select>
 </div>

 {/* Intermediate Marks */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Intermediate Marks (%)
 </label>
 <input
 type="number"
 min="0"
 max="100"
 step="0.01"
 value={personalInfo.intermediate_marks || ''}
 onChange={(e) => onChange('intermediate_marks', e.target.value)}
 className={`w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm ${validationErrors.intermediate_marks ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`}
 placeholder="Enter your intermediate marks"
 />
 {validationErrors.intermediate_marks && (
 <p className="text-red-600 text-sm mt-1">{validationErrors.intermediate_marks}</p>
 )}
 </div>
 </>
 ) : (
 // University Fields
 <>
 {/* University Name (Non-editable) */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 University Name
 </label>
 <input
 type="text"
 value={personalInfo.university_name || ''}
 disabled
 className="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-slate-100 text-slate-500 cursor-not-allowed font-medium text-sm"
 />
 <p className="text-xs text-slate-400 mt-1">University name cannot be changed</p>
 </div>

 {/* Contact Person Name */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Contact Person Name
 </label>
 <input
 type="text"
 value={personalInfo.contact_person || ''}
 onChange={(e) => onChange('contact_person', e.target.value)}
 className={`w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm ${validationErrors.contact_person ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`}
 placeholder="Enter contact person name"
 />
 {validationErrors.contact_person && (
 <p className="text-red-600 text-sm mt-1">{validationErrors.contact_person}</p>
 )}
 </div>

 {/* Email (Non-editable) */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Email Address
 </label>
 <input
 type="email"
 value={personalInfo.email || ''}
 disabled
 className="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-slate-100 text-slate-500 cursor-not-allowed font-medium text-sm"
 />
 <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
 </div>

 {/* Phone Number */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Phone Number
 </label>
 <input
 type="tel"
 value={personalInfo.phone || ''}
 onChange={(e) => onChange('phone', e.target.value)}
 className={`w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm ${validationErrors.phone ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`}
 placeholder="03XXXXXXXXX"
 maxLength="11"
 />
 {validationErrors.phone ? (
 <p className="text-red-600 text-sm mt-1">{validationErrors.phone}</p>
 ) : (
 <p className="text-xs text-slate-400 mt-1">Pakistani format (11 digits)</p>
 )}
 </div>

 {/* University Address */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 University Address
 </label>
 <textarea
 value={personalInfo.address || ''}
 onChange={(e) => onChange('address', e.target.value)}
 rows="3"
 className={`w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm ${validationErrors.address ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`}
 placeholder="Enter university address"
 />
 {validationErrors.address && (
 <p className="text-red-600 text-sm mt-1">{validationErrors.address}</p>
 )}
 </div>

 {/* Established Year (Non-editable) */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Established Year
 </label>
 <input
 type="number"
 value={personalInfo.established_year || ''}
 disabled
 className="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-slate-100 text-slate-500 cursor-not-allowed font-medium text-sm"
 />
 <p className="text-xs text-slate-400 mt-1">Established year cannot be changed</p>
 </div>

 {/* University Type */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 University Type
 </label>
 <select
 value={personalInfo.university_type_id || ''}
 onChange={(e) => onChange('university_type_id', e.target.value)}
 className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm cursor-pointer"
 >
 <option value="">Select University Type</option>
 {universityTypes.map(type => (
 <option key={type.id} value={type.id}>{type.name}</option>
 ))}
 </select>
 </div>
 </>
 )}
 </div>

 {/* Action Buttons */}
 <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-slate-100">
 <button
 onClick={onCancel}
 disabled={!hasUnsavedChanges || saving}
 className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2 px-5 rounded-xl border border-slate-200 hover:border-slate-300 active:scale-[0.98] transition-all duration-300 ease-out shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
 >
 Cancel
 </button>
 <button
 onClick={onSave}
 disabled={!hasUnsavedChanges || saving}
 className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-2 px-5 rounded-xl shadow-md shadow-primary-500/10 hover:shadow-lg hover:shadow-primary-500/20 active:scale-[0.98] transition-all duration-300 ease-out flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {saving ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
 <span>Saving...</span>
 </>
 ) : (
 <>
 <CheckIcon className="h-4 w-4" />
 <span>Save Changes</span>
 </>
 )}
 </button>
 </div>
 </div>
 );
};

// Account Settings Section Component
const AccountSettingsSection = ({
 isStudent,
 passwordData,
 onPasswordChange,
 onChangePassword,
 onDeleteAccount,
 saving,
 passwordValidationErrors = {},
 showOldPassword,
 setShowOldPassword,
 showNewPassword,
 setShowNewPassword,
 showConfirmPassword,
 setShowConfirmPassword
}) => {
 return (
 <div className="space-y-6">
 {/* Change Password Section */}
 <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
 <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center space-x-2">
 <KeyIcon className="h-6 w-6 text-primary-600" />
 <span>Change Password</span>
 </h2>

 <div className="space-y-4">
 {/* Old Password */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Current Password
 </label>
 <div className="relative">
 <input
 type={showOldPassword ? 'text' : 'password'}
 value={passwordData.oldPassword}
 onChange={(e) => onPasswordChange('oldPassword', e.target.value)}
 className={`w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm pr-10 ${passwordValidationErrors.oldPassword ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`}
 placeholder="Enter your current password"
 />
 <button
 type="button"
 onClick={() => setShowOldPassword(!showOldPassword)}
 className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
 >
 {showOldPassword ? (
 <EyeSlashIcon className="h-5 w-5" />
 ) : (
 <EyeIcon className="h-5 w-5" />
 )}
 </button>
 </div>
 {passwordValidationErrors.oldPassword && (
 <p className="text-red-600 text-sm mt-1">{passwordValidationErrors.oldPassword}</p>
 )}
 </div>

 {/* New Password */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 New Password
 </label>
 <div className="relative">
 <input
 type={showNewPassword ? 'text' : 'password'}
 value={passwordData.newPassword}
 onChange={(e) => onPasswordChange('newPassword', e.target.value)}
 className={`w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm pr-10 ${passwordValidationErrors.newPassword ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`}
 placeholder="Enter your new password"
 />
 <button
 type="button"
 onClick={() => setShowNewPassword(!showNewPassword)}
 className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
 >
 {showNewPassword ? (
 <EyeSlashIcon className="h-5 w-5" />
 ) : (
 <EyeIcon className="h-5 w-5" />
 )}
 </button>
 </div>
 {passwordValidationErrors.newPassword ? (
 <p className="text-red-600 text-sm mt-1">{passwordValidationErrors.newPassword}</p>
 ) : (
 <p className="text-xs text-slate-400 mt-1">Password must be at least 8 characters long</p>
 )}
 </div>

 {/* Confirm New Password */}
 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 Confirm New Password
 </label>
 <div className="relative">
 <input
 type={showConfirmPassword ? 'text' : 'password'}
 value={passwordData.confirmPassword}
 onChange={(e) => onPasswordChange('confirmPassword', e.target.value)}
 className={`w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm pr-10 ${passwordValidationErrors.confirmPassword ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`}
 placeholder="Confirm your new password"
 />
 <button
 type="button"
 onClick={() => setShowConfirmPassword(!showConfirmPassword)}
 className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
 >
 {showConfirmPassword ? (
 <EyeSlashIcon className="h-5 w-5" />
 ) : (
 <EyeIcon className="h-5 w-5" />
 )}
 </button>
 </div>
 {passwordValidationErrors.confirmPassword && (
 <p className="text-red-600 text-sm mt-1">{passwordValidationErrors.confirmPassword}</p>
 )}
 </div>
 </div>

 {/* Change Password Button */}
 <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
 <button
 onClick={onChangePassword}
 disabled={!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword || saving}
 className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md shadow-primary-500/10 hover:shadow-lg hover:shadow-primary-500/20 active:scale-[0.98] transition-all duration-300 ease-out flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {saving ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
 <span>Changing...</span>
 </>
 ) : (
 <>
 <KeyIcon className="h-4 w-4" />
 <span>Change Password</span>
 </>
 )}
 </button>
 </div>
 </div>

 {/* Delete Account Section */}
 <div className="bg-rose-50/10 border border-rose-200/60 border-l-4 border-l-rose-500 rounded-2xl p-6">
 <h2 className="text-xl font-bold text-rose-950 mb-4 flex items-center space-x-2">
 <ExclamationTriangleIcon className="h-6 w-6 text-rose-600" />
 <span>Danger Zone</span>
 </h2>

 <div className="bg-rose-50/50 border border-rose-100 rounded-xl p-4 mb-4">
 <h3 className="text-lg font-bold text-rose-800 mb-2">
 Delete {isStudent ? 'Student' : 'University'} Account
 </h3>
 <p className="text-rose-700 text-sm mb-4 leading-relaxed">
 This action cannot be undone. This will permanently delete your {isStudent ? 'student' : 'university'} account
 and remove all associated data from our servers.
 </p>
 <ul className="text-rose-750 text-sm space-y-1.5 mb-4">
 <li className="flex items-center gap-1.5">• All your personal information will be deleted</li>
 {isStudent ? (
 <>
 <li className="flex items-center gap-1.5">• Your application history will be removed</li>
 <li className="flex items-center gap-1.5">• Your profile and preferences will be lost</li>
 </>
 ) : (
 <>
 <li className="flex items-center gap-1.5">• All university programs will be removed</li>
 <li className="flex items-center gap-1.5">• Campus facilities information will be deleted</li>
 <li className="flex items-center gap-1.5">• Student applications to your university will be affected</li>
 </>
 )}
 <li className="flex items-center gap-1.5">• This action is irreversible</li>
 </ul>
 </div>

 <div className="flex justify-end">
 <button
 onClick={onDeleteAccount}
 className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md shadow-red-500/10 hover:shadow-lg hover:shadow-red-500/20 active:scale-[0.98] transition-all duration-300 ease-out flex items-center space-x-2"
 >
 <TrashIcon className="h-4 w-4" />
 <span>Delete Account</span>
 </button>
 </div>
 </div>
 </div>
 );
};

// Delete Account Modal Component
const DeleteAccountModal = ({
 isStudent,
 userEmail,
 deleteConfirmText,
 setDeleteConfirmText,
 onConfirm,
 onCancel,
 saving
}) => {
 const expectedText = userEmail || '';

 return (
 <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
 <div className="bg-white border border-slate-200 shadow-2xl rounded-2xl max-w-md w-full overflow-hidden transition-all duration-300">
 <div className="p-6">
 {/* Header */}
 <div className="flex items-center space-x-3 mb-4">
 <div className="flex-shrink-0 p-2 bg-rose-50 rounded-xl">
 <ExclamationTriangleIcon className="h-8 w-8 text-rose-600 animate-pulse" />
 </div>
 <div>
 <h3 className="text-lg font-bold text-slate-800">
 Delete {isStudent ? 'Student' : 'University'} Account
 </h3>
 <p className="text-xs font-semibold text-slate-400">
 This action is irreversible
 </p>
 </div>
 </div>

 {/* Warning */}
 <div className="bg-rose-50/50 border border-rose-100 rounded-xl p-4 mb-4">
 <p className="text-rose-800 text-sm font-bold mb-1">
 ⚠️ Warning: Permanent Deletion
 </p>
 <p className="text-rose-700 text-xs leading-relaxed">
 All your data will be permanently removed from our servers. You will lose access immediately.
 </p>
 </div>

 {/* Confirmation Input */}
 <div className="mb-6">
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 To confirm, type <span className="font-bold text-rose-600 font-mono">"{expectedText}"</span> below:
 </label>
 <input
 type="text"
 value={deleteConfirmText}
 onChange={(e) => setDeleteConfirmText(e.target.value)}
 className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl placeholder-slate-350 text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 font-medium transition-premium text-sm"
 placeholder={expectedText}
 />
 </div>

 {/* Action Buttons */}
 <div className="flex justify-end space-x-3">
 <button
 onClick={onCancel}
 disabled={saving}
 className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2 px-5 rounded-xl border border-slate-200 hover:border-slate-300 active:scale-[0.98] transition-all duration-300 ease-out shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
 >
 Cancel
 </button>
 <button
 onClick={onConfirm}
 disabled={deleteConfirmText !== expectedText || saving}
 className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md shadow-red-500/10 hover:shadow-lg hover:shadow-red-500/20 active:scale-[0.98] transition-all duration-300 ease-out flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {saving ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
 <span>Deleting...</span>
 </>
 ) : (
 <>
 <TrashIcon className="h-4 w-4" />
 <span>Delete Account</span>
 </>
 )}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
};

export default Settings;
